// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { TransactionType, projectSchema } from '@/project/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const updateProjectResponseSchema = z
  .object({
    data: projectSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, project: data }));

type UpdateProjectResponse = z.infer<typeof updateProjectResponseSchema>;

export interface UpdateProjectLineItem {
  id?: string;
  itemId: string;
  transactionType: TransactionType;
  count: number;
  details: string | null;
  locationId?: string | null;
  aggregationKey: string;
  startOn: DateTime | null;
  endOn: DateTime | null;
}

export interface UpdateProjectParameters {
  name?: string;
  start?: DateTime | null;
  end?: DateTime | null;
  customerId?: string | null;
  defaultLocationId?: string | null;
  lineItems?: UpdateProjectLineItem[];
  isArchived?: boolean;
  totalContractValue?: Currency | null;
  description?: string | null;
  previousProjectId?: string | null;
  billingAddress?: string | null;
  billingContactName?: string | null;
  billingContactEmail?: string | null;
  billingContactPhone?: string | null;
  purchaseOrderNumber?: string | null;
  paymentTerm?: string | null;
  discount?: Currency | null;
  memo?: string | null;
  contractSignedOn?: DateTime | null;
  installDate?: DateTime | null;
  siteVisitDate?: DateTime | null;
  trainingDate?: DateTime | null;
  goLiveDate?: DateTime | null;
  acceptanceDate?: DateTime | null;
  workflowId?: string | null;
  workflowStageId?: string | null;
}

/**
 * Updates an existing project in the remote API.
 *
 * @param parameters.customerId - The ID of the customer associated with the
 * project.
 * @param parameters.defaultLocationId - The ID of the default location for the
 * project.
 */
export async function updateProject(
  key: string,
  parameters: UpdateProjectParameters
): Promise<UpdateProjectResponse> {
  const url = `/v1/project/${key}`;

  // Build the request payload, only including workflow fields if they're provided
  const payload = {
    name: parameters.name,
    endOn: serializeDateForAPI(parameters.end),
    startOn: serializeDateForAPI(parameters.start),
    isArchived: parameters.isArchived,
    customerId: parameters.customerId,
    defaultLocationId: parameters.defaultLocationId,
    description: parameters.description,
    previousProjectId: parameters.previousProjectId,
    totalContractValue: serializeOptionalCurrencyForAPI(
      parameters.totalContractValue
    ),
    billingAddress: parameters.billingAddress,
    billingContactName: parameters.billingContactName,
    billingContactEmail: parameters.billingContactEmail,
    billingContactPhone: parameters.billingContactPhone,
    purchaseOrderNumber: parameters.purchaseOrderNumber,
    paymentTerm: parameters.paymentTerm,
    discount: serializeOptionalCurrencyForAPI(parameters.discount),
    memo: parameters.memo,
    contractSignedOn: serializeDateForAPI(parameters.contractSignedOn),
    installDate: serializeDateForAPI(parameters.installDate),
    siteVisitDate: serializeDateForAPI(parameters.siteVisitDate),
    trainingDate: serializeDateForAPI(parameters.trainingDate),
    goLiveDate: serializeDateForAPI(parameters.goLiveDate),
    acceptanceDate: serializeDateForAPI(parameters.acceptanceDate),
    lineItems: parameters.lineItems?.map((lineItem) => ({
      id: lineItem.id,
      itemId: lineItem.itemId,
      quantity: lineItem.count,
      details: lineItem.details,
      locationId: lineItem.locationId,
      transactionType: lineItem.transactionType,
      aggregationKey: lineItem.aggregationKey,
      startOn: serializeDateForAPI(lineItem.startOn),
      endOn: serializeDateForAPI(lineItem.endOn),
    })),
    workflowId: parameters.workflowId,
    workflowStageId: parameters.workflowStageId,
  };

  const response = await api.patch(url, payload);

  return parseResponse(updateProjectResponseSchema, response.data, {
    schemaName: 'UpdateProject',
    endpoint: url,
  });
}
